import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Bluetooth, Usb, Printer, Unplug } from "lucide-react";
import { toast } from "sonner";
import {
  getPrinterSettings, savePrinterSettings, connectBluetooth, connectUSB,
  disconnectPrinter, printerConnected, printerName, supportsBluetooth, supportsUSB, printThermal,
  reconnectPrinter, savedPrinterName,
} from "../lib/thermal";

export function PrinterDialog({ open, onOpenChange, lastSale, cashier }) {
  const [settings, setSettings] = useState(getPrinterSettings());
  const [busy, setBusy] = useState(false);
  const [, force] = useState(0);

  const update = (s) => { setSettings(s); savePrinterSettings(s); };

  const connect = async (type) => {
    setBusy(true);
    try {
      const name = type === "bluetooth" ? await connectBluetooth() : await connectUSB();
      update({ ...settings, type });
      toast.success(`Printer terhubung: ${name}`);
    } catch (e) {
      toast.error(e?.message || "Gagal menghubungkan printer");
    } finally { setBusy(false); force((n) => n + 1); }
  };

  const reconnect = async () => {
    setBusy(true);
    try {
      const name = await reconnectPrinter();
      if (name) toast.success(`Tersambung ke ${name}`);
      else toast.error("Printer tersimpan tidak ditemukan / di luar jangkauan");
    } catch (e) {
      toast.error(e?.message || "Gagal menyambung ulang");
    } finally { setBusy(false); force((n) => n + 1); }
  };

  const testPrint = async () => {
    const sample = lastSale || {
      number: "TEST-0001", created_at: new Date().toISOString(), items: [{ name: "Contoh Barang", qty: 1, price: 10000, discount: 0, subtotal: 10000 }],
      subtotal: 10000, total_discount: 0, total: 10000, paid_amount: 10000, change: 0, payment_method: "cash",
    };
    try { await printThermal(sample, cashier); toast.success("Tes cetak terkirim"); }
    catch (e) { toast.error(e?.message || "Gagal mencetak"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="printer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5 text-orange-500" /> Printer Thermal</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs text-slate-500 mb-1.5">Lebar kertas</div>
            <div className="grid grid-cols-2 gap-2">
              {[58, 80].map((w) => (
                <button key={w} onClick={() => update({ ...settings, width: w })} data-testid={`printer-width-${w}`}
                  className={`py-2 rounded-lg border font-medium ${settings.width === w ? "bg-orange-500 text-white border-orange-500" : "border-slate-200 text-slate-600"}`}>
                  {w} mm
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1.5">Koneksi</div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" disabled={busy || !supportsBluetooth()} onClick={() => connect("bluetooth")} data-testid="printer-connect-bluetooth">
                <Bluetooth className="h-4 w-4 mr-1.5" /> Bluetooth
              </Button>
              <Button variant="outline" disabled={busy || !supportsUSB()} onClick={() => connect("usb")} data-testid="printer-connect-usb">
                <Usb className="h-4 w-4 mr-1.5" /> USB
              </Button>
            </div>
            {!supportsBluetooth() && !supportsUSB() && (
              <p className="text-xs text-amber-700 mt-2">Browser ini tidak mendukung Web Bluetooth/USB. Gunakan Chrome/Edge (HTTPS). Struk akan dicetak lewat dialog browser.</p>
            )}
          </div>
          <div className={`rounded-lg px-3 py-2.5 flex items-center justify-between ${printerConnected() ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid="printer-status">
            <span>{printerConnected() ? `Terhubung: ${printerName()}` : "Belum ada printer terhubung — fallback ke print browser"}</span>
            {printerConnected() && (
              <button onClick={() => { disconnectPrinter(); force((n) => n + 1); }} className="text-xs flex items-center gap-1 text-rose-600"><Unplug className="h-3.5 w-3.5" /> Putus</button>
            )}
          </div>
          {!printerConnected() && savedPrinterName() && (
            <Button variant="outline" onClick={reconnect} disabled={busy} data-testid="printer-reconnect-saved"
              className="w-full border-orange-300 text-orange-700 hover:bg-orange-50">
              <Bluetooth className="h-4 w-4 mr-1.5" /> Sambungkan ulang tersimpan: {savedPrinterName()}
            </Button>
          )}
          <div className="flex gap-2">
            <Button onClick={testPrint} disabled={!printerConnected()} className="bg-orange-500 hover:bg-orange-600 flex-1" data-testid="printer-test-print">Tes Cetak</Button>
            <Button variant="outline" onClick={() => window.print()} disabled={!lastSale} className="flex-1" data-testid="printer-browser-print">Cetak Ulang (Browser)</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
